/**
 * AnonymizationPreviewPanel — dry-run / pré-visualização de impacto (Fatia 1
 * motor ADR-110, 10/Jun/2026). Acende o honest-defer da Tela 3 B5.
 *
 * Renderiza o plano REAL computado pelo AnonymizationPreviewService:
 *   - bloqueadores em destaque (LegalHold com motivo; já anonimizada; em curso);
 *   - cascata síncrona entidade-a-entidade com ação campo-a-campo (mapa v1);
 *   - satélites diferidos com contagens HONESTAS (workers Fatia 3+ — o painel
 *     NÃO finge cobertura total);
 *   - selo do modo de snapshot (hasheado vs claro — decisão ratificada 10/Jun).
 *
 * Zero mutação: o preview é advisory; a execução irreversível continua no
 * fluxo create → execute com typing-to-confirm.
 */

import {
  DEFERRED_ENTITY_LABEL,
  FIELD_ACTION_META,
  PREVIEW_ENTITY_LABEL,
  type AnonymizationPreviewPlan,
} from './types';

interface Props {
  plan: AnonymizationPreviewPlan;
}

export function AnonymizationPreviewPanel({ plan }: Props): JSX.Element {
  return (
    <div className="anon-preview" data-testid="anon-preview-panel">
      <div className="anon-preview__head">
        <strong>Plano de anonimização (dry-run)</strong>
        <span className="anon-preview__meta">
          mapa v{plan.fieldMapVersion} · snapshot forense{' '}
          {plan.snapshotMode === 'hashed' ? 'hasheado (eliminação real)' : 'em claro (DPO vê)'}
        </span>
      </div>

      {plan.blockers.length > 0 ? (
        <div className="anon-preview__blockers" role="alert">
          <strong>Execução bloqueada:</strong>
          <ul>
            {plan.blockers.map((b) => (
              <li key={b.code + (b.legalHoldId ?? b.anonymizationId ?? '')}>{b.detail}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="anon-preview__ok">
          Sem bloqueadores — a entidade pode ser anonimizada neste nível.
        </p>
      )}

      <div className="anon-preview__entities">
        {plan.entities.map((entity) => (
          <div key={entity.entityType + entity.entityId} className="anon-preview__entity">
            <div className="anon-preview__entity-head">
              <span className="anon-preview__entity-type">
                {PREVIEW_ENTITY_LABEL[entity.entityType]}
              </span>
              <span className="anon-preview__entity-label">{entity.label}</span>
              <code className="anon-preview__entity-id">{entity.entityId}</code>
            </div>
            <ul className="anon-preview__fields">
              {entity.fields.map((f) => (
                <li key={f.field} className="anon-preview__field">
                  <code className="anon-preview__field-name">{f.field}</code>
                  <span
                    className={`anon-preview__action anon-preview__action--${FIELD_ACTION_META[f.action].tone}`}
                  >
                    {FIELD_ACTION_META[f.action].label}
                  </span>
                  <span className="anon-preview__field-detail">{f.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="anon-preview__deferred">
        <strong>Fora desta operação (tratados por rotinas automáticas posteriores):</strong>
        <ul>
          {plan.deferred.map((d) => (
            <li key={d.entityType}>
              {DEFERRED_ENTITY_LABEL[d.entityType] ?? d.entityType}: <strong>{d.count}</strong>{' '}
              registro(s) — {d.coveredBy}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
